import { createApiHooks, useGenericGet } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  TemplateMapping,
  TemplateMappingCreatePayload,
  TemplateMappingUpdatePayload,
  TemplateMappingGetResponse,
  TemplateMappingListResponse,
} from '../types/template-mapping.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';

const useTemplateMappingBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    if (!activeTenant?.id) return '';
    return `/v1/admin/tenants/${activeTenant.id}/template-mappings`;
  }, [activeTenant?.id]);
};

export const useTemplateMappingHooks = () => {
  const basePath = useTemplateMappingBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<
    TemplateMapping,
    TemplateMappingGetResponse,
    TemplateMappingCreatePayload,
    TemplateMappingUpdatePayload
  >({
    basePath,
    queryKey: ['TemplateMappings', 'Tenant', activeTenant?.id || ''],
    apiClient,
  });
};

export const useTemplateMappingApi = () => {
  const hooks = useTemplateMappingHooks();

  return {
    useGet: hooks.useGet,
    useCreate: hooks.useCreate,
    useUpdate: hooks.useUpdate,
    useDelete: hooks.useDelete,
    useList: (params?: UseListParams, queryOptions?: any) => hooks.useList(params, queryOptions),
  };
};

export const useTemplateMappingList = (params?: UseListParams, queryOptions?: any) => {
  const basePath = useTemplateMappingBasePath();
  const { activeTenant } = useActiveTenant();

  return useGenericGet<TemplateMappingListResponse>(
    basePath,
    ['TemplateMappings', 'Tenant', activeTenant?.id || '', 'list', params] as any,
    apiClient,
    {
      enabled: !!activeTenant?.id && !!basePath,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      ...queryOptions,
    }
  );
};

export const useTemplateMappingGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useTemplateMappingApi();
  return useGet(id, queryOptions);
};

export const useTemplateMappingCreate = (mutationOptions?: any) => {
  const { useCreate } = useTemplateMappingApi();
  return useCreate(mutationOptions);
};

export const useTemplateMappingUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useTemplateMappingApi();
  return useUpdate(mutationOptions);
};

export const useTemplateMappingDelete = (mutationOptions?: any) => {
  const { useDelete } = useTemplateMappingApi();
  return useDelete(mutationOptions);
};
